// Developer-only model picker — the UI half.
//
// A plain dropdown over the dev server's candidate list, so a developer can
// run any model through the real turn pipeline. The choice lives in
// localStorage (it is a developer's per-browser experiment, not a user
// setting) and rides the chat transport as `model`, which the server ignores
// unless its own dev picker is armed.
//
// This file never reaches the product: shipping bundles compile with
// __PONEVA_DEV_TOOLS__ = false and esbuild drops the component entirely
// (test/ui-bundle-dev-tools.mjs proves it). Only the --dev-tools build in
// .dev-ui/, served by an armed local server, contains it.

import { useEffect, useState } from 'react';

const STORAGE_KEY = 'poneva.localModel';

export function readLocalModel(): string {
  if (typeof localStorage === 'undefined') return '';
  try {
    return localStorage.getItem(STORAGE_KEY) ?? '';
  } catch {
    return '';
  }
}

function writeLocalModel(value: string): void {
  if (typeof localStorage === 'undefined') return;
  try {
    if (value) localStorage.setItem(STORAGE_KEY, value);
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* private mode */
  }
}

interface DevModels {
  models: Array<{ name: string; provider: string }>;
}

export function ModelPickerImpl() {
  const [payload, setPayload] = useState<DevModels | null>(null);
  const [selected, setSelected] = useState<string>('');

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch('/api/v1/models', { credentials: 'same-origin' });
        if (!res.ok) return; // not armed → the section simply does not exist
        const data = (await res.json()) as DevModels;
        if (cancelled || !Array.isArray(data?.models)) return;
        setPayload(data);
        setSelected(readLocalModel());
      } catch {
        /* not armed / offline */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!payload) return null;

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-dashed border-amber-400/70 bg-amber-50/40 p-3">
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium">Model (local only)</span>
        <span className="rounded bg-amber-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-900">
          dev
        </span>
      </div>
      <select
        aria-label="Serving model (local only)"
        value={selected}
        onChange={(e) => {
          const value = (e.target as HTMLSelectElement).value;
          setSelected(value);
          writeLocalModel(value);
        }}
        className="border-border bg-background w-full rounded-md border px-2 py-1.5 text-sm"
      >
        <option value="">Server default</option>
        {payload.models.map((m) => (
          <option key={m.name} value={m.name}>
            {m.name} — {m.provider}
          </option>
        ))}
      </select>
      <p className="text-muted-foreground text-xs">
        Applies to your next message in this browser. Only exists on a local dev server.
      </p>
    </div>
  );
}

// Build-time gate: shipping builds fold this to the null component and
// tree-shake everything above it out of the bundle.
declare const __PONEVA_DEV_TOOLS__: boolean;

export const ModelPicker = __PONEVA_DEV_TOOLS__ ? ModelPickerImpl : (): null => null;
